import { useEffect, useRef } from "react";
import { formatMessage } from "stream-chat";
import { useLocation } from "react-router-dom";
import {
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import { useNavigate } from "Navigation/useNavigate";
import { useSearchParams } from "Navigation/useSearchParams";

export const CHAT_THREAD_SEARCH_PARAM = "thread";

/** The channel URL's search string with `?thread` set to `threadId`, or removed when it is null. */
export const getSearchWithThreadParam = (
  search: string,
  threadId: string | null,
) => {
  const next = new URLSearchParams(search);
  if (threadId) {
    next.set(CHAT_THREAD_SEARCH_PARAM, threadId);
  } else {
    next.delete(CHAT_THREAD_SEARCH_PARAM);
  }
  const nextSearch = next.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

/**
 * Two-way sync between the open thread in the Stream channel state and the `?thread=<messageId>`
 * search param on the channel URL. Must run inside `<Channel>`.
 *
 * The URL is the source of truth for navigation: opening a thread PUSHES a history entry, so the
 * thread behaves like a page on the stack — the in-app back button, the browser back button and
 * the Android hardware back all return to the conversation. Switching to another thread while
 * one is open REPLACES that entry, so the stack is never more than one thread deep and closing
 * always lands on the conversation. Deep links straight into a thread open it once the channel
 * is loaded.
 *
 * Both directions are handled in ONE effect so a stale render can't fight the other direction:
 * the effect only runs when either side's thread id changes, and a ref remembers the URL value
 * it last saw. If the URL changed, the URL wins (open/close in Stream); otherwise Stream changed
 * and the URL follows.
 */
export const useChatThreadUrlSync = () => {
  const { channel, thread } = useChannelStateContext();
  const { openThread, closeThread } = useChannelActionContext();
  const { client } = useChatContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const threadIdInUrl = searchParams.get(CHAT_THREAD_SEARCH_PARAM);
  const openThreadId = thread?.id;

  const lastSeenUrlThreadIdRef = useRef<string | null | undefined>(undefined);

  // Read through refs so the effect keys ONLY on the two thread ids — location and the Stream
  // callbacks change identity on unrelated renders and must not re-run the sync.
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const openThreadRef = useRef(openThread);
  openThreadRef.current = openThread;
  const closeThreadRef = useRef(closeThread);
  closeThreadRef.current = closeThread;

  useEffect(() => {
    const urlChanged = lastSeenUrlThreadIdRef.current !== threadIdInUrl;
    lastSeenUrlThreadIdRef.current = threadIdInUrl;

    const { pathname, search } = locationRef.current;

    const navigateWithThreadParam = (
      threadId: string | null,
      options?: { replace: boolean },
    ) => {
      navigateRef.current(
        { pathname, search: getSearchWithThreadParam(search, threadId) },
        options,
      );
    };

    if (!urlChanged) {
      // Stream changed: a thread was opened from a message action, or Stream closed one itself.
      if (openThreadId && openThreadId !== threadIdInUrl) {
        // Thread → thread switch replaces the current thread entry instead of stacking on it.
        navigateWithThreadParam(openThreadId, { replace: !!threadIdInUrl });
      } else if (!openThreadId && threadIdInUrl) {
        navigateWithThreadParam(null, { replace: true });
      }
      return;
    }

    // URL changed: back/forward, deep link, or our own back button.
    if (!threadIdInUrl) {
      if (openThreadId) {
        closeThreadRef.current();
      }
      return;
    }

    if (threadIdInUrl === openThreadId) {
      return;
    }

    let cancelled = false;

    const openFromUrl = async () => {
      const localMessage = channel.state.findMessage(threadIdInUrl);
      if (localMessage) {
        openThreadRef.current(localMessage);
        return;
      }
      // Not in the loaded window (deep link into an old message) — fetch it.
      const { message } = await client.getMessage(threadIdInUrl);
      if (cancelled) {
        return;
      }
      if (message.cid !== channel.cid) {
        throw new Error("Thread parent belongs to another channel");
      }
      // A reply can't be a thread parent; opening it would show an empty thread.
      if (message.parent_id) {
        throw new Error("Thread parent is itself a reply");
      }
      openThreadRef.current(formatMessage(message));
    };

    openFromUrl().catch(() => {
      if (!cancelled) {
        navigateWithThreadParam(null, { replace: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [threadIdInUrl, openThreadId, channel, client]);
};
